import { DateTime } from '@ksyos/date-time';
import { Card, Table, TableBody, TableContainer, TableHead, TableHeader, TableRow } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ActivityType } from '../../../../../common/enums/activityType';
import type { EpisodeCategory } from '../../../../../common/enums/episodeCategory';
import type { ServiceType } from '../../../../../common/enums/serviceType';
import type { IFetchResult } from '../../../../store/fetch';
import type { ICompensationPayoutLog } from '../../../interfaces/compensationPayoutLog';
import { fetchJson } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import { ActivityCompensationPayoutsLogEntry } from './ActivityCompensationPayoutLogEntry';

interface IActivityCompensationPayoutsHistoryProps {
    activityType: string;
    compensationType: ActivityType;
    employmentId?: string;
    episodeCategory: EpisodeCategory;
    organizationId: string;
    serviceGroupSettingId?: string;
    serviceId: string;
    serviceName: string;
    serviceType?: ServiceType;
}
export function ActivityCompensationPayoutsHistory(props: IActivityCompensationPayoutsHistoryProps) {
    const [compensationHistoryLogs, setCompensationHistoryLogs] = useState<JSX.Element[]>([]);

    useEffect(() => {
        getCompensationHistoryLogs();
    }, []);

    return (
        <Card>
            <Card.Header
                title={`Logboek ${translateKey(`activityTypeEnum.${props.activityType}`)} ${props.serviceName}`}
            />
            <TableContainer>
                <Table>
                    <TableHead
                        key={`${props.serviceType}_${props.serviceName}_${props.compensationType}_${props.serviceId}`}
                    >
                        <TableRow>
                            <TableHeader>{translateKey('compensationHistoryHeaders.modificationDate')}</TableHeader>
                            <TableHeader>{translateKey('compensationHistoryHeaders.modifiedBy')}</TableHeader>
                            <TableHeader>
                                {translateKey('compensationHistoryHeaders.compensationChangeDesc')}
                            </TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>{compensationHistoryLogs}</TableBody>
                </Table>
            </TableContainer>
        </Card>
    );

    async function getCompensationHistoryLogs() {
        const logs: JSX.Element[] = [];
        const compensationPayoutsUrl = (entityId: string) =>
            `/api/compensationPayoutLogs/?serviceId=${props.serviceId}&entityId=${entityId}&activityType=${props.activityType}`;
        const compensationPayoutsLogsForOrganization = await fetchJson(
            compensationPayoutsUrl(props.organizationId),
            'GET'
        );
        let allCompensationPayouts = compensationPayoutsLogsForOrganization.json;
        let serviceGroupSettingResponse: IFetchResult;

        if (props.employmentId) {
            const compensationPayoutsLogsForEmployment = await fetchJson(
                compensationPayoutsUrl(props.employmentId),
                'GET'
            );
            if (compensationPayoutsLogsForEmployment) {
                allCompensationPayouts = allCompensationPayouts.concat(compensationPayoutsLogsForEmployment.json);
            }
        }

        if (props.serviceGroupSettingId) {
            serviceGroupSettingResponse = await fetchJson(
                `/api/serviceGroupSettings/${props.serviceGroupSettingId}`,
                'GET'
            );
            if (serviceGroupSettingResponse) {
                const compensationPayoutsLogsForGroup = await fetchJson(
                    compensationPayoutsUrl(serviceGroupSettingResponse.json.groupId),
                    'GET'
                );

                if (compensationPayoutsLogsForGroup) {
                    allCompensationPayouts = allCompensationPayouts.concat(compensationPayoutsLogsForGroup.json);
                }
            }
        }

        allCompensationPayouts
            .sort((a: ICompensationPayoutLog, b: ICompensationPayoutLog) => {
                return DateTime.from(b.created_at).diff(DateTime.from(a.created_at));
            })
            .forEach((item: ICompensationPayoutLog) => {
                if (item) {
                    logs.push(
                        <ActivityCompensationPayoutsLogEntry
                            compensationPayoutsLog={item}
                            groupId={serviceGroupSettingResponse?.json?.groupId}
                            key={`${item.id}`}
                            {...props}
                        />
                    );
                }
            });
        setCompensationHistoryLogs(logs);
    }
}
